import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import type { UnitSystem } from './obd/pids.ts';

// Display preferences, and the date formatting that reads them.
//
// Everything here is presentation only. Stored data stays metric and ISO, so
// changing a preference reformats old records rather than rewriting them.

export type DateFormat = 'auto' | 'iso' | 'us' | 'intl';

export const DATE_FORMATS: { value: DateFormat; label: string; example: string }[] = [
  { value: 'auto', label: 'Locale', example: 'as your system prefers' },
  { value: 'iso', label: 'ISO', example: '2026-07-28' },
  { value: 'us', label: 'US', example: 'Jul 28, 2026' },
  { value: 'intl', label: 'Day first', example: '28 Jul 2026' },
];

interface Prefs {
  units: UnitSystem;
  dateFormat: DateFormat;
  setUnits: (u: UnitSystem) => void;
  setDateFormat: (d: DateFormat) => void;
}

const UNITS_KEY = 'bayone.units';
const DATE_KEY = 'bayone.dateFormat';

const PreferencesContext = createContext<Prefs | null>(null);

/** Default to the locale's conventions rather than making everyone change them. */
function initialUnits(): UnitSystem {
  const saved = localStorage.getItem(UNITS_KEY);
  if (saved === 'metric' || saved === 'imperial') return saved;
  const region = new Intl.Locale(navigator.language).maximize().region;
  return region && ['US', 'LR', 'MM'].includes(region) ? 'imperial' : 'metric';
}

function initialDateFormat(): DateFormat {
  const saved = localStorage.getItem(DATE_KEY);
  return DATE_FORMATS.some((f) => f.value === saved) ? (saved as DateFormat) : 'auto';
}

export function PreferencesProvider({ children }: { children: ReactNode }) {
  const [units, setUnitsState] = useState<UnitSystem>(initialUnits);
  const [dateFormat, setDateFormatState] = useState<DateFormat>(initialDateFormat);

  const setUnits = useCallback((u: UnitSystem) => {
    setUnitsState(u);
    localStorage.setItem(UNITS_KEY, u);
  }, []);

  const setDateFormat = useCallback((d: DateFormat) => {
    setDateFormatState(d);
    localStorage.setItem(DATE_KEY, d);
  }, []);

  const value = useMemo(
    () => ({ units, dateFormat, setUnits, setDateFormat }),
    [units, dateFormat, setUnits, setDateFormat],
  );
  return <PreferencesContext.Provider value={value}>{children}</PreferencesContext.Provider>;
}

export function usePrefs(): Prefs {
  const ctx = useContext(PreferencesContext);
  if (!ctx) throw new Error('usePrefs must be used inside PreferencesProvider');
  return ctx;
}

// ---------- date formatting ----------

/**
 * Parse the two shapes that reach the UI.
 *
 * Recall dates are ISO days, normalised by the bridge from NHTSA's dd/mm/yyyy.
 * Scan timestamps come from SQLite as "YYYY-MM-DD HH:MM:SS" in UTC with no zone
 * marker, which every JS engine would otherwise read as local time and shift by
 * the offset. Both are handled here so no caller has to remember either quirk.
 */
function parse(value: string): Date | null {
  const trimmed = value.trim();

  // Date only: pin to local midnight so the calendar day cannot slip backwards.
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    const d = new Date(`${trimmed}T00:00:00`);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  const iso = trimmed.includes('T') ? trimmed : trimmed.replace(' ', 'T') + 'Z';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** A calendar day, e.g. a recall publish date. */
export function formatDay(value: string | null | undefined, format: DateFormat): string {
  if (!value) return '—';
  const d = parse(value);
  if (!d) return value;

  switch (format) {
    case 'iso':
      return toIsoDay(d);
    case 'us':
      return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    case 'intl':
      return d.toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: 'numeric' });
    default:
      return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  }
}

/** A day plus clock time, e.g. when a scan ran. */
export function formatStamp(value: string | null | undefined, format: DateFormat): string {
  if (!value) return '—';
  const d = parse(value);
  if (!d) return value;

  const time = d.toLocaleTimeString(format === 'iso' ? 'en-GB' : undefined, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: format === 'us' ? true : format === 'iso' || format === 'intl' ? false : undefined,
  });

  switch (format) {
    case 'iso':
      return `${toIsoDay(d)} ${time}`;
    case 'us':
      return `${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}, ${time}`;
    case 'intl':
      return `${d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}, ${time}`;
    default:
      return `${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}, ${time}`;
  }
}

/** Local calendar day as ISO, without the UTC shift toISOString would apply. */
function toIsoDay(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
